/**
 * @zendir/ui - Property Configuration System
 *
 * Enterprise-grade property-to-icon/status/unit mapping system.
 * Provides automatic icon, status, and formatting for common telemetry properties.
 *
 * AstroUXDS Compliance:
 * - Uses official Astro icon names
 * - Status colors per AstroUXDS status system
 * - Dual-coding (color + shape) for accessibility
 *
 * Features:
 * - Auto-derive status from value + thresholds
 * - Contextual icons for space operations properties
 * - Unit formatting with SI prefixes
 * - Extensible configuration
 *
 * @example
 * ```tsx
 * import { getPropertyConfig, deriveStatus, PROPERTY_PRESETS } from '@zendir/ui';
 *
 * // Get config for a known property
 * const tempConfig = getPropertyConfig('temperature');
 * // { icon: 'thermal', unit: '°C', thresholds: { caution: 60, critical: 80 }, ... }
 *
 * // Derive status from value
 * const status = deriveStatus(75, tempConfig.thresholds);
 * // 'caution'
 * ```
 */

import type { StatusLevel } from "../../utils";
import type { AstroIconName } from "../display/AstroIcon";

// =============================================================================
// Types
// =============================================================================

/**
 * Threshold configuration for automatic status derivation
 * Values are compared: value >= threshold triggers that status (high thresholds)
 * or value <= threshold triggers that status (low thresholds)
 */
export interface StatusThresholds {
  /** Value at or above which status becomes 'critical' (high is bad) */
  critical?: number;
  /** Value at or above which status becomes 'serious' (high is bad) */
  serious?: number;
  /** Value at or above which status becomes 'caution' (high is bad) */
  caution?: number;
  /** Value at or below which status becomes 'caution' (low is bad) */
  cautionLow?: number;
  /** Value at or below which status becomes 'serious' (low is bad) */
  seriousLow?: number;
  /** Value at or below which status becomes 'critical' (low is bad) */
  criticalLow?: number;
}

/**
 * Complete property configuration
 */
export interface PropertyConfig {
  /** Display name (sentence case per AstroUXDS) */
  label: string;
  /** Astro icon name */
  icon: AstroIconName;
  /** Unit of measurement */
  unit?: string;
  /** Precision for number formatting */
  precision?: number;
  /** Status thresholds for auto-derivation */
  thresholds?: StatusThresholds;
  /** Category for grouping */
  category?: PropertyCategory;
  /** Description for tooltips */
  description?: string;
  /** Format function override */
  format?: (value: number) => string;
}

/**
 * Property categories for organization
 */
export type PropertyCategory =
  | "power"
  | "thermal"
  | "attitude"
  | "communications"
  | "propulsion"
  | "navigation"
  | "payload"
  | "health"
  | "orbital"
  | "general";

/**
 * Property key - either a preset name or custom string
 */
export type PropertyKey = keyof typeof PROPERTY_PRESETS | (string & {});

// =============================================================================
// Property Presets
// =============================================================================

/**
 * Predefined property configurations for common space operations telemetry
 *
 * These provide sensible defaults that can be overridden per-use.
 */
export const PROPERTY_PRESETS = {
  // === Power System ===
  battery: {
    label: "Battery",
    icon: "battery-full" as AstroIconName,
    unit: "%",
    precision: 1,
    category: "power" as PropertyCategory,
    // Low values are bad for battery (use *Low thresholds)
    thresholds: { criticalLow: 10, seriousLow: 20, cautionLow: 30 },
    description: "Battery state of charge",
  },
  voltage: {
    label: "Voltage",
    icon: "propulsion-power" as AstroIconName,
    unit: "V",
    precision: 2,
    category: "power" as PropertyCategory,
    thresholds: { criticalLow: 24, cautionLow: 26 },
    description: "Bus voltage",
  },
  current: {
    label: "Current",
    icon: "propulsion-power" as AstroIconName,
    unit: "A",
    precision: 2,
    category: "power" as PropertyCategory,
    description: "Current draw",
  },
  power: {
    label: "Power",
    icon: "propulsion-power" as AstroIconName,
    unit: "W",
    precision: 1,
    category: "power" as PropertyCategory,
    description: "Power consumption or generation",
  },
  solarPower: {
    label: "Solar",
    icon: "solar" as AstroIconName,
    unit: "W",
    precision: 1,
    category: "power" as PropertyCategory,
    description: "Solar array output",
  },
  load: {
    label: "Load",
    icon: "propulsion-power" as AstroIconName,
    unit: "W",
    precision: 1,
    category: "power" as PropertyCategory,
    description: "Power load",
  },

  // === Thermal ===
  temperature: {
    label: "Temperature",
    icon: "thermal" as AstroIconName,
    unit: "°C",
    precision: 1,
    category: "thermal" as PropertyCategory,
    thresholds: {
      critical: 80,
      serious: 70,
      caution: 60,
      cautionLow: -20,
      criticalLow: -40,
    },
    description: "Component temperature",
  },
  heater: {
    label: "Heater",
    icon: "thermal" as AstroIconName,
    unit: "",
    category: "thermal" as PropertyCategory,
    description: "Heater status",
  },

  // === Attitude ===
  roll: {
    label: "Roll",
    icon: "explore" as AstroIconName,
    unit: "°",
    precision: 2,
    category: "attitude" as PropertyCategory,
    thresholds: { critical: 45, caution: 30 },
    description: "Roll angle",
  },
  pitch: {
    label: "Pitch",
    icon: "explore" as AstroIconName,
    unit: "°",
    precision: 2,
    category: "attitude" as PropertyCategory,
    thresholds: { critical: 45, caution: 30 },
    description: "Pitch angle",
  },
  yaw: {
    label: "Yaw",
    icon: "explore" as AstroIconName,
    unit: "°",
    precision: 2,
    category: "attitude" as PropertyCategory,
    thresholds: { critical: 45, caution: 30 },
    description: "Yaw angle",
  },
  pointingError: {
    label: "Pointing error",
    icon: "explore" as AstroIconName,
    unit: "°",
    precision: 3,
    category: "attitude" as PropertyCategory,
    thresholds: { critical: 1.0, serious: 0.5, caution: 0.1 },
    description: "Attitude pointing error",
  },
  angularVelocity: {
    label: "Angular velocity",
    icon: "explore" as AstroIconName,
    unit: "°/s",
    precision: 3,
    category: "attitude" as PropertyCategory,
    thresholds: { critical: 5.0, caution: 2.0 },
    description: "Rotation rate",
  },

  // === Communications ===
  signalStrength: {
    label: "Signal",
    icon: "antenna-receive" as AstroIconName,
    unit: "dBm",
    precision: 1,
    category: "communications" as PropertyCategory,
    thresholds: { criticalLow: -100, cautionLow: -85 },
    description: "Received signal strength",
  },
  dataRate: {
    label: "Data rate",
    icon: "antenna-transmit" as AstroIconName,
    unit: "Mbps",
    precision: 2,
    category: "communications" as PropertyCategory,
    description: "Communication data rate",
  },
  packetLoss: {
    label: "Packet loss",
    icon: "netcom" as AstroIconName,
    unit: "%",
    precision: 2,
    category: "communications" as PropertyCategory,
    thresholds: { critical: 10, serious: 5, caution: 1 },
    description: "Communication packet loss rate",
  },
  snr: {
    label: "SNR",
    icon: "antenna" as AstroIconName,
    unit: "dB",
    precision: 1,
    category: "communications" as PropertyCategory,
    thresholds: { criticalLow: 5, cautionLow: 10 },
    description: "Signal-to-noise ratio",
  },
  queue: {
    label: "Queue",
    icon: "netcom" as AstroIconName,
    unit: "pkts",
    precision: 0,
    category: "communications" as PropertyCategory,
    thresholds: { critical: 1000, caution: 500 },
    description: "Packet queue depth",
  },

  // === Propulsion ===
  fuelLevel: {
    label: "Fuel",
    icon: "propulsion-power" as AstroIconName,
    unit: "%",
    precision: 1,
    category: "propulsion" as PropertyCategory,
    thresholds: { critical: 5, serious: 10, caution: 20 },
    description: "Propellant remaining",
  },
  deltaV: {
    label: "Delta-V",
    icon: "propulsion-power" as AstroIconName,
    unit: "m/s",
    precision: 1,
    category: "propulsion" as PropertyCategory,
    description: "Available delta-V budget",
  },
  thrustLevel: {
    label: "Thrust",
    icon: "propulsion-power" as AstroIconName,
    unit: "N",
    precision: 2,
    category: "propulsion" as PropertyCategory,
    description: "Current thrust output",
  },

  // === Navigation ===
  altitude: {
    label: "Altitude",
    icon: "altitude" as AstroIconName,
    unit: "km",
    precision: 1,
    category: "orbital" as PropertyCategory,
    thresholds: { criticalLow: 200, cautionLow: 250 },
    description: "Orbital altitude",
  },
  velocity: {
    label: "Velocity",
    icon: "altitude" as AstroIconName,
    unit: "km/s",
    precision: 3,
    category: "orbital" as PropertyCategory,
    description: "Orbital velocity",
  },
  inclination: {
    label: "Inclination",
    icon: "altitude" as AstroIconName,
    unit: "°",
    precision: 2,
    category: "orbital" as PropertyCategory,
    description: "Orbital inclination",
  },
  eccentricity: {
    label: "Eccentricity",
    icon: "altitude" as AstroIconName,
    unit: "",
    precision: 6,
    category: "orbital" as PropertyCategory,
    description: "Orbital eccentricity",
  },

  // === Payload ===
  payloadStatus: {
    label: "Payload",
    icon: "payload" as AstroIconName,
    unit: "",
    category: "payload" as PropertyCategory,
    description: "Payload operational status",
  },
  storageUsed: {
    label: "Storage",
    icon: "hardware" as AstroIconName,
    unit: "%",
    precision: 1,
    category: "payload" as PropertyCategory,
    thresholds: { critical: 95, serious: 90, caution: 80 },
    description: "Onboard storage usage",
  },

  // === Health ===
  cpuUsage: {
    label: "CPU",
    icon: "processor" as AstroIconName,
    unit: "%",
    precision: 1,
    category: "health" as PropertyCategory,
    thresholds: { critical: 95, serious: 85, caution: 75 },
    description: "Processor utilization",
  },
  memoryUsage: {
    label: "Memory",
    icon: "processor" as AstroIconName,
    unit: "%",
    precision: 1,
    category: "health" as PropertyCategory,
    thresholds: { critical: 95, serious: 85, caution: 75 },
    description: "Memory utilization",
  },
  uptime: {
    label: "Uptime",
    icon: "mission" as AstroIconName,
    unit: "s",
    precision: 0,
    category: "health" as PropertyCategory,
    description: "System uptime",
  },

  // === General ===
  count: {
    label: "Count",
    icon: "list" as AstroIconName,
    unit: "",
    precision: 0,
    category: "general" as PropertyCategory,
    description: "Item count",
  },
  percentage: {
    label: "Percentage",
    icon: "info" as AstroIconName,
    unit: "%",
    precision: 1,
    category: "general" as PropertyCategory,
    description: "Percentage value",
  },
  duration: {
    label: "Duration",
    icon: "schedule" as AstroIconName,
    unit: "s",
    precision: 0,
    category: "general" as PropertyCategory,
    description: "Time duration",
  },
} as const satisfies Record<string, PropertyConfig>;

// =============================================================================
// Category Icons (for section headers)
// =============================================================================

export const CATEGORY_ICONS: Record<PropertyCategory, AstroIconName> = {
  power: "propulsion-power",
  thermal: "thermal",
  attitude: "explore",
  communications: "antenna",
  propulsion: "propulsion-power",
  navigation: "gps-fixed",
  payload: "payload",
  health: "favorite",
  orbital: "altitude",
  general: "info",
};

export const CATEGORY_LABELS: Record<PropertyCategory, string> = {
  power: "Power",
  thermal: "Thermal",
  attitude: "Attitude",
  communications: "Communications",
  propulsion: "Propulsion",
  navigation: "Navigation",
  payload: "Payload",
  health: "Health",
  orbital: "Orbital",
  general: "General",
};

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get property configuration by key
 * Returns preset config or a default config for unknown properties
 */
export function getPropertyConfig(key: PropertyKey): PropertyConfig {
  const preset = PROPERTY_PRESETS[key as keyof typeof PROPERTY_PRESETS];
  if (preset) {
    return preset;
  }

  // Return default config for unknown properties
  return {
    label: formatPropertyLabel(key),
    icon: "info",
    unit: "",
    precision: 2,
    category: "general",
    description: key,
  };
}

/**
 * Format a property key into a display label (sentence case)
 */
export function formatPropertyLabel(key: string): string {
  // Handle camelCase → Sentence case
  const words = key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Derive status level from a value and thresholds
 *
 * @param value - Current value
 * @param thresholds - Status thresholds
 * @returns StatusLevel
 *
 * @example
 * ```tsx
 * deriveStatus(75, { caution: 60, critical: 80 }) // 'caution'
 * deriveStatus(25, { criticalLow: 10, cautionLow: 30 }) // 'caution'
 * deriveStatus(50, { caution: 60, critical: 80 }) // 'normal'
 * ```
 */
export function deriveStatus(
  value: number | undefined | null,
  thresholds?: StatusThresholds,
): StatusLevel {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return "off";
  }

  if (!thresholds) {
    return "normal";
  }

  // Check high thresholds (value >= threshold is bad)
  if (thresholds.critical !== undefined && value >= thresholds.critical) {
    return "critical";
  }
  if (thresholds.serious !== undefined && value >= thresholds.serious) {
    return "serious";
  }
  if (thresholds.caution !== undefined && value >= thresholds.caution) {
    return "caution";
  }

  // Check low thresholds (value <= threshold is bad)
  if (thresholds.criticalLow !== undefined && value <= thresholds.criticalLow) {
    return "critical";
  }
  if (thresholds.seriousLow !== undefined && value <= thresholds.seriousLow) {
    return "serious";
  }
  if (thresholds.cautionLow !== undefined && value <= thresholds.cautionLow) {
    return "caution";
  }

  return "normal";
}

/**
 * Derive status for battery specifically (low is bad)
 */
export function deriveBatteryStatus(level: number | undefined): StatusLevel {
  if (level === undefined || level === null) return "off";
  if (level <= 10) return "critical";
  if (level <= 20) return "serious";
  if (level <= 30) return "caution";
  return "normal";
}

/**
 * Format a value with its property config
 */
export function formatPropertyValue(
  value: number | undefined | null,
  config: PropertyConfig,
): string {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return "--";
  }

  if (config.format) {
    return config.format(value);
  }

  const precision = config.precision ?? 2;
  const formatted = value.toFixed(precision);
  const unit = config.unit ?? "";

  return unit
    ? `${formatted}${unit.startsWith("°") || unit.startsWith("%") ? "" : " "}${unit}`
    : formatted;
}

/**
 * Create a custom property config by merging with a preset
 */
export function createPropertyConfig(
  baseKey: PropertyKey,
  overrides: Partial<PropertyConfig>,
): PropertyConfig {
  const base = getPropertyConfig(baseKey);
  return { ...base, ...overrides };
}

/**
 * Get all properties in a category
 */
export function getPropertiesByCategory(
  category: PropertyCategory,
): Array<{ key: string; config: PropertyConfig }> {
  return Object.entries(PROPERTY_PRESETS)
    .filter(([_, config]) => config.category === category)
    .map(([key, config]) => ({ key, config }));
}
